import { ThemeEvents, CartUpdatedEvent } from '@theme/events';
import { formatMoney, formatCents } from '@theme/utilities';

const GIFT_PROPERTY = '_cart_progress_bar_gift';
const SOURCE = 'cart-progress-bar';
const GIFT_DISMISSED_EVENT = 'cart-progress-bar:gift-dismissed';
const DISMISSED_STORAGE_KEY = 'cart-progress-bar-dismissed';

const getShopifyCurrencyRate = () => {
  const raw = window.Shopify?.currency?.rate;

  if (raw === undefined || raw === null || raw === '') return 1;

  const rate = Number(raw);

  return Number.isFinite(rate) && rate > 0 ? rate : 1;
};

const getDismissedGifts = () => {
  try {
    return JSON.parse(sessionStorage.getItem(DISMISSED_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
};

const getDismissalKey = (blockId, stepKey) => `${blockId}:${stepKey}`;

const isGiftDismissed = (blockId, stepKey) => {
  const dismissed = getDismissedGifts();

  return Boolean(dismissed[getDismissalKey(blockId, stepKey)]);
};

const dismissGift = (blockId, stepKey) => {
  const dismissed = getDismissedGifts();
  dismissed[getDismissalKey(blockId, stepKey)] = true;
  sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(dismissed));
};

const clearDismissedGift = (blockId, stepKey) => {
  const dismissed = getDismissedGifts();

  delete dismissed[getDismissalKey(blockId, stepKey)];
  sessionStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify(dismissed));
};

const clearAllDismissedGifts = () => {
  sessionStorage.removeItem(DISMISSED_STORAGE_KEY);
};

const fetchCartJson = async () => {
  const response = await fetch('/cart.js', { cache: 'no-store' });

  if (!response.ok) return null;

  return response.json();
};

const getCartMetric = (cart, basedOn) => {
  if (basedOn === 'quantity') {
    return cart.item_count || 0;
  }

  return cart.total_price || 0;
};

const getStepThreshold = (step, basedOn, rate = 1) => {
  if (basedOn === 'quantity') {
    return step.thresholdQuantity || step.threshold || 0;
  }

  if (step.freeShipping) {
    if (step.thresholdMarketCents > 0) return step.thresholdMarketCents;

    const fallbackShopCents = step.thresholdShopCents ?? 0;

    if (fallbackShopCents > 0) return Math.round(fallbackShopCents * rate);

    return 0;
  }

  const shopCents = step.thresholdShopCents ?? step.threshold ?? 0;

  return Math.round(shopCents * rate);
};

const getMilestoneThresholds = (config, rate = 1) => {
  const milestones = config?.milestones || config?.steps || [];

  return milestones.map((milestone) => getStepThreshold(milestone, config.basedOn, rate));
};

const getMilestoneByKey = (config, stepKey) => {
  const milestones = config?.milestones || [];

  return milestones.find((milestone) => milestone.key === stepKey) || null;
};

const applyProgressBarPlaceholders = (template, { missing, amount }) => {
  if (!template) return '';

  let result = template;

  if (amount != null && amount !== '') {
    result = result.split('#amount#').join(String(amount));
  }

  if (missing != null && missing !== '') {
    result = result.split('#').join(String(missing));
  }

  return result;
};

const formatProgressBarAmount = (config, cents) => {
  if (config.basedOn === 'quantity') {
    return String(cents);
  }

  return formatMoney(Math.max(0, Math.round(cents)));
};

const formatProgressBarLabelAmount = (config, cents) => {
  if (config.basedOn === 'quantity') {
    return String(cents);
  }

  const roundedCents = Math.round(Math.max(0, cents) / 100) * 100;
  const template = Theme.moneyFormat;
  const currency = Theme.currency;

  return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
    if (typeof placeholder !== 'string') return '';
    if (placeholder === 'currency') return currency;
    if (!placeholder.startsWith('amount')) return '';

    let thousandsSeparator = ',';
    let decimalSeparator = '.';

    if (placeholder === 'amount_with_comma_separator' || placeholder === 'amount_no_decimals_with_comma_separator') {
      thousandsSeparator = '.';
      decimalSeparator = ',';
    } else if (placeholder === 'amount_no_decimals_with_space_separator' || placeholder === 'amount_with_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = placeholder === 'amount_with_space_separator' ? ',' : '.';
    } else if (placeholder === 'amount_with_period_and_space_separator') {
      thousandsSeparator = ' ';
      decimalSeparator = '.';
    } else if (placeholder === 'amount_with_apostrophe_separator') {
      thousandsSeparator = "'";
      decimalSeparator = '.';
    }

    return formatCents(roundedCents, thousandsSeparator, decimalSeparator, 0);
  });
};

const getActiveStepPhrase = (config, metric, thresholds) => {
  const texts = config.stepTexts || {};
  const milestones = config.milestones || [];
  const [t1 = 0, t2 = 0, t3 = 0] = thresholds;
  const showStep1 = milestones.some((milestone) => milestone.key === 'step1') && t1 > 0;
  const showStep2 = milestones.some((milestone) => milestone.key === 'step2') && t2 > 0;
  const showStep3 = milestones.some((milestone) => milestone.key === 'step3') && t3 > 0;
  const allCompleted =
    (!showStep1 || metric >= t1) && (!showStep2 || metric >= t2) && (!showStep3 || metric >= t3) && (showStep1 || showStep2 || showStep3);

  if (allCompleted) {
    return texts.completed || '';
  }

  if (showStep3 && t3 > 0 && metric >= t2 && metric < t3) {
    return applyProgressBarPlaceholders(texts.step3, {
      missing: formatProgressBarAmount(config, t3 - metric),
    });
  }

  if (showStep2 && t2 > 0 && metric >= t1 && metric < t2) {
    return applyProgressBarPlaceholders(texts.step2, {
      missing: formatProgressBarAmount(config, t2 - metric),
    });
  }

  if (showStep1 && t1 > 0 && metric < t1) {
    return applyProgressBarPlaceholders(texts.step1, {
      missing: formatProgressBarAmount(config, t1 - metric),
    });
  }

  return texts.completed || '';
};

const updateProgressBarTexts = (element, cart, config) => {
  if (!config) return;

  const rate = getShopifyCurrencyRate();
  const metric = getCartMetric(cart, config.basedOn);
  const thresholds = getMilestoneThresholds(config, rate);
  const phraseNode = element.querySelector('[data-ref="progress-bar-phrase"]');
  const phrase = getActiveStepPhrase(config, metric, thresholds);

  if (phraseNode) {
    if (phrase) {
      phraseNode.innerHTML = phrase;
      phraseNode.hidden = false;
    } else {
      phraseNode.innerHTML = '';
      phraseNode.hidden = true;
    }
  }

  const labels = config.stepLabels || {};

  for (const [stepKey, template] of Object.entries(labels)) {
    if (!template) continue;

    const labelNode = element.querySelector(`[data-ref="milestone-label"][data-step-key="${stepKey}"]`);

    if (!labelNode) continue;

    const milestone = getMilestoneByKey(config, stepKey);
    const threshold = milestone ? getStepThreshold(milestone, config.basedOn, rate) : 0;
    const amount = threshold > 0 ? formatProgressBarLabelAmount(config, threshold) : '';

    labelNode.innerHTML = applyProgressBarPlaceholders(template, { amount });
  }
};

const computeMilestoneProgress = (metric, thresholds, positions, stepsCount) => {
  if (!thresholds.length || stepsCount === 0) return 0;

  const [t1 = 0, t2 = 0, t3 = 0] = thresholds;
  const [p1 = 50, p2 = 50, p3 = 50] = positions;

  if (stepsCount === 3 && t3 > 0) {
    if (metric >= t3) return 100;

    if (metric >= t2 && t2 > 0 && t3 > t2) {
      const ratio = ((metric - t2) / (t3 - t2)) * 100;

      return p2 + (ratio * (p3 - p2)) / 100;
    }

    if (metric >= t1 && t1 > 0 && t2 > t1) {
      const ratio = ((metric - t1) / (t2 - t1)) * 100;

      return p1 + (ratio * (p2 - p1)) / 100;
    }

    if (t1 > 0) {
      const ratio = (metric / t1) * 100;

      return (ratio * p1) / 100;
    }

    return 0;
  }

  if (stepsCount === 2 && t2 > 0) {
    if (metric >= t2) return 100;

    if (metric >= t1 && t1 > 0 && t2 > t1) {
      const ratio = ((metric - t1) / (t2 - t1)) * 100;

      return p1 + (ratio * (p2 - p1)) / 100;
    }

    if (t1 > 0) {
      const ratio = (metric / t1) * 100;

      return (ratio * p1) / 100;
    }

    return 0;
  }

  if (t1 > 0) {
    if (metric >= t1) return 100;

    const ratio = (metric / t1) * 100;

    return (ratio * p1) / 100;
  }

  return 0;
};

const getItemProperties = (item) => item.properties || {};

const findGiftLine = (items, stepKey, blockId) => {
  if (!items?.length) return null;

  const index = items.findIndex((item) => {
    const props = getItemProperties(item);

    if (props[GIFT_PROPERTY] !== stepKey) return false;

    if (!blockId) return true;

    return String(props._cart_progress_bar_block) === String(blockId);
  });

  if (index === -1) return null;

  return { item: items[index], line: index + 1 };
};

const hasVariantInCart = (items, variantId, productId, stepKey, blockId) => {
  if (!items?.length) return false;

  return items.some((item) => {
    const props = getItemProperties(item);
    const isSameGiftLine =
      stepKey &&
      blockId &&
      props[GIFT_PROPERTY] === stepKey &&
      String(props._cart_progress_bar_block) === String(blockId);

    if (isSameGiftLine) return false;

    if (variantId && item.variant_id === variantId) return true;

    return Boolean(productId && item.product_id === productId);
  });
};

const getCartDrawerSections = () => {
  const cartDrawer = document.querySelector('cart-drawer');

  return cartDrawer?.dataset.sectionId ? [cartDrawer.dataset.sectionId] : [];
};

const dispatchCartUpdated = (result, itemCountDelta = 0) => {
  const currentItemCount = document.querySelector('cart-icon')?.getCurrentItemCount();
  let newItemCount;

  if (currentItemCount != null) {
    newItemCount = currentItemCount + itemCountDelta;
  }

  document.dispatchEvent(
    new CartUpdatedEvent(
      {},
      {
        itemCount: newItemCount,
        source: SOURCE,
        sections: result.sections,
      },
    ),
  );
};

const addGiftLines = async (lines) => {
  if (!lines.length) return null;

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: lines,
      sections: getCartDrawerSections().join(','),
      sections_url: window.location.pathname,
    }),
  });

  const result = await response.json();

  if (!response.ok) return null;

  return result;
};

const removeGiftLine = async (lineIndex) => {
  const response = await fetch(Theme.routes.cart_change_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      line: lineIndex,
      quantity: 0,
      sections: getCartDrawerSections().join(','),
      sections_url: window.location.pathname,
    }),
  });

  if (!response.ok) return null;

  return response.json();
};

const readBlockConfig = (element) => {
  const configNode = element.querySelector('[data-ref="progress-bar-config"]');

  if (!configNode?.textContent?.trim()) return null;

  try {
    return JSON.parse(configNode.textContent);
  } catch {
    return null;
  }
};

const updateProgressBarVisual = (element, cart, config) => {
  if (!config || config.layout !== 'milestones') return;

  const fill = element.querySelector('[data-ref="progress-bar-fill"]');

  if (!fill) return;

  const rate = getShopifyCurrencyRate();
  const metric = getCartMetric(cart, config.basedOn);
  const thresholds = getMilestoneThresholds(config, rate);
  const stepsCount = config.stepsCount || thresholds.filter((threshold) => threshold > 0).length;
  const positions = (config.stepPositions || [50, 50, 50]).slice(0, 3);
  const progress = computeMilestoneProgress(metric, thresholds, positions, stepsCount);
  const clampedProgress = Math.min(100, Math.max(0, progress));

  fill.style.width = `${clampedProgress}%`;

  const milestones = config.milestones || [];

  for (const milestone of milestones) {
    const marker = element.querySelector(`[data-step-key="${milestone.key}"]`);

    if (!marker) continue;

    const threshold = getStepThreshold(milestone, config.basedOn, rate);
    marker.classList.toggle('cart-progress-bar__marker-cell--completed', threshold > 0 && metric >= threshold);
  }

  updateProgressBarTexts(element, cart, config);
};

const updateAllProgressBarVisuals = (cart) => {
  if (!cart) return;

  for (const element of document.querySelectorAll('cart-progress-bar')) {
    const config = readBlockConfig(element);

    if (!config) continue;

    if (config.layout === 'milestones') {
      updateProgressBarVisual(element, cart, config);
      continue;
    }

    updateProgressBarTexts(element, cart, config);
  }
};

const syncBlockGifts = async (element, cart, config) => {
  if (!config?.enableAutoAdd || !config.steps?.length) return false;

  const rate = getShopifyCurrencyRate();
  const metric = getCartMetric(cart, config.basedOn);
  const linesToAdd = [];

  for (const step of config.steps) {
    const variantId = step.variantId;

    if (!variantId) continue;

    const threshold = getStepThreshold(step, config.basedOn, rate);
    const reached = metric >= threshold;
    const existingGift = findGiftLine(cart.items, step.key, config.blockId);

    if (!reached) {
      clearDismissedGift(config.blockId, step.key);

      if (existingGift) {
        const result = await removeGiftLine(existingGift.line);

        if (result) {
          dispatchCartUpdated(result, -(existingGift.item.quantity || 1));
          return true;
        }
      }

      continue;
    }

    if (isGiftDismissed(config.blockId, step.key)) continue;
    if (existingGift) continue;
    if (hasVariantInCart(cart.items, variantId, step.productId, step.key, config.blockId)) continue;

    linesToAdd.push({
      id: variantId,
      quantity: 1,
      properties: {
        [GIFT_PROPERTY]: step.key,
        _cart_progress_bar_block: String(config.blockId),
      },
    });
  }

  if (!linesToAdd.length) return false;

  const result = await addGiftLines(linesToAdd);

  if (!result) return false;

  dispatchCartUpdated(result, linesToAdd.length);

  return true;
};

// ponytail: shared lock across duplicate module evaluations (asset_url ?v= vs relative import)
const getSyncState = () => {
  const key = '__fsCartProgressBarSync';

  if (!window[key]) window[key] = { inProgress: false };

  return window[key];
};

const syncAllProgressBars = async () => {
  const syncState = getSyncState();

  if (syncState.inProgress) return;

  syncState.inProgress = true;

  try {
    let cart = await fetchCartJson();

    if (!cart?.items) return;

    updateAllProgressBarVisuals(cart);

    const elements = document.querySelectorAll('cart-progress-bar[data-auto-add="true"]');
    let hasChanges = false;

    do {
      hasChanges = false;

      for (const element of elements) {
        const config = readBlockConfig(element);

        if (!config?.enableAutoAdd) continue;

        const changed = await syncBlockGifts(element, cart, config);

        if (changed) {
          hasChanges = true;
          cart = await fetchCartJson();

          if (!cart?.items) break;

          updateAllProgressBarVisuals(cart);
          break;
        }
      }
    } while (hasChanges && cart?.items);
  } finally {
    syncState.inProgress = false;
  }
};

const onCartUpdated = (event) => {
  const data = event.detail?.data;

  if (data?.source === SOURCE) return;

  if (!data?.giftRemoved) {
    clearAllDismissedGifts();
  }

  // Exposé pour que l'offre supérieure attende la fin des ajouts cadeaux
  window.__cartProgressSync = syncAllProgressBars();
};

const onGiftDismissed = (event) => {
  const { blockId, stepKey } = event.detail || {};

  if (!blockId || !stepKey) return;

  dismissGift(blockId, stepKey);
};

if (!customElements.get('cart-progress-bar')) {
  customElements.define(
    'cart-progress-bar',
    class CartProgressBar extends HTMLElement {
      connectedCallback() {
        fetchCartJson().then((cart) => {
          const config = readBlockConfig(this);

          if (!cart || !config) return;

          if (config.layout === 'milestones') {
            updateProgressBarVisual(this, cart, config);
            return;
          }

          updateProgressBarTexts(this, cart, config);
        });
      }
    },
  );
}

// ponytail: listeners once even if the module URL is evaluated twice
if (!window.__fsCartProgressBarListeners) {
  window.__fsCartProgressBarListeners = true;

  document.addEventListener(ThemeEvents.cartUpdated, onCartUpdated);
  document.addEventListener(ThemeEvents.cartOpen, syncAllProgressBars);
  document.addEventListener(GIFT_DISMISSED_EVENT, onGiftDismissed);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncAllProgressBars);
  } else {
    syncAllProgressBars();
  }
}
